import { createElement } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { useToastStore } from "@/components";
import { CartUnavailableMessage } from "@/domains/careplan/components/cart-unavailable-message";
import { careplanQueryOptions, getCartLines } from "@/domains/careplan/factories";
import { cartCreateMutation } from "@/domains/careplan/models/graphql";
import {
  CartLineNetworkError,
  CartLineValidationError,
} from "@/domains/careplan/mutations/cart-line-errors";
import { storefrontApi } from "@/modules/network";
import { useCartStore } from "@/stores";
import type {
  Cart,
  CartCreateMutation,
  CartCreateMutationVariables,
  CartLineInput,
} from "@/types/storefront-api.gen";
import { attachErrorContext } from "@/utils/error-reporting";

const CART_UNAVAILABLE_TOAST_DURATION_MS = 10_000;

export type CartCreationReportContext = {
  careplanId: number;
  careplanVersion: number;
  suspectVariantIds: string[];
  monthlySellingPlanId?: string;
};

export type CreateCareplanCartVariables = CartCreateMutationVariables & {
  reportContext: CartCreationReportContext;
};

export const useCreateCareplanCart = () => {
  const queryClient = useQueryClient();
  const setRecommendedCartLines = useCartStore((state) => {
    return state.setRecommendedCartLines;
  });
  const setCartCreationFailed = useCartStore((state) => {
    return state.setCartCreationFailed;
  });
  const pushToast = useToastStore((state) => {
    return state.pushToast;
  });

  const saveNewCartToClientStorage = (newCart: Cart) => {
    setRecommendedCartLines(getCartLines(newCart));
    sessionStorage.setItem("careplanCartId", newCart.id);
  };

  return useMutation({
    mutationFn: createCareplanCart,
    onSuccess: async (response) => {
      setCartCreationFailed(false);
      saveNewCartToClientStorage(response?.cartCreate?.cart as Cart);

      await queryClient.invalidateQueries({
        queryKey: careplanQueryOptions.cart.queryKey,
      });
    },
    onError: async () => {
      setCartCreationFailed(true);

      await pushToast({
        type: "error",
        title: "Cart Unavailable",
        message: createElement(CartUnavailableMessage),
        duration: CART_UNAVAILABLE_TOAST_DURATION_MS,
      });
    },
  });
};

const toLineList = (lines: CartLineInput[] | CartLineInput | null | undefined) => {
  if (!lines) {
    return [];
  }

  return Array.isArray(lines) ? lines : [lines];
};

export const createCareplanCart = async ({
  reportContext,
  ...variables
}: CreateCareplanCartVariables) => {
  const { discountCodes, lines } = variables.input;
  const requestContext = {
    lines: toLineList(lines).map(({ merchandiseId, quantity, sellingPlanId }) => {
      return { merchandiseId, quantity, sellingPlanId };
    }),
    discountCodes,
    ...reportContext,
  };

  let response: Awaited<ReturnType<typeof storefrontApi.request<CartCreateMutation>>>;

  try {
    response = await storefrontApi.request<CartCreateMutation>(cartCreateMutation, { variables });
  } catch (error) {
    throw attachErrorContext(
      new CartLineNetworkError("Failed to reach Shopify while creating the cart", {
        cause: error,
      }),
      { tags: { cart_creation_failure: "network" }, extra: requestContext },
    );
  }

  const payload = response.data?.cartCreate;
  const userErrors = payload?.userErrors ?? [];

  if (payload?.cart && userErrors.length === 0) {
    return response.data;
  }

  throw attachErrorContext(
    new CartLineValidationError(userErrors[0]?.message ?? "Shopify did not return a cart"),
    {
      tags: { cart_creation_failure: userErrors.length > 0 ? "user_errors" : "no_cart" },
      extra: { userErrors, warnings: payload?.warnings ?? [], ...requestContext },
    },
  );
};
